/**
 * O anexo da mensagem é um ÁUDIO?
 *
 * ## Para que serve (ponto 9 da Gisele, 20/10/2026)
 *
 * "Quando a última mensagem do cliente for um áudio, a lista de conversas deve
 * mostrar 'Áudio'." Hoje a lista mostra o nome do arquivo -- algo como
 * `683f1a2c9b.ogg` --, que não diz nada a quem está decidindo qual conversa
 * atender primeiro.
 *
 * ## Por que esta lista é mais larga que a do Chat
 *
 * `components/Chat` reconhece áudio por `data:audio` ou `.ogg|.mp3`, e
 * continua assim de propósito: lá a decisão é **tocar** o arquivo, e o
 * `CustomAudioPlayer` monta o blob como `audio/ogg; codecs=opus`. Alargar
 * aquela regra faria um `.m4a` cair num player que não sabe decodificá-lo --
 * troca um rótulo ruim por um áudio que não toca.
 *
 * Aqui a decisão é só **rotular**. Errar para mais custa a palavra "Áudio" num
 * arquivo que não era áudio; errar para menos devolve o nome do arquivo, que é
 * o problema que o ponto 9 relata. As extensões são as que a api já considera
 * áudio ao ENVIAR pela Meta (`EnvioPelaMeta`), mais as que ela grava ao
 * receber (`MidiaDaMeta`: ogg, mp3, m4a).
 */
const EXTENSOES_DE_AUDIO = ['ogg', 'oga', 'opus', 'mp3', 'm4a', 'aac', 'amr', 'wav'];

export const ehAudio = (archive) => {
    const texto = String(archive || '').trim();

    if (texto === '') return false;

    // O áudio gravado pelo operador chega como data URL, sem extensão nenhuma.
    if (texto.startsWith('data:audio')) return true;

    // A query string de um link assinado viria depois da extensão e faria o
    // `endsWith` falhar -- o `archive` do chat é um caminho, mas o do WhatsApp
    // já passou por URL assinada antes.
    const semQuery = texto.split('?')[0];
    const extensao = semQuery.split('.').pop().toLowerCase();

    return EXTENSOES_DE_AUDIO.includes(extensao);
};

/**
 * O que a lista de conversas mostra no lugar do nome do arquivo.
 *
 * Devolve nulo quando não há anexo -- aí a prévia continua sendo o texto da
 * mensagem, como sempre foi.
 */
/**
 * O anexo é uma IMAGEM?
 *
 * ## Para que serve (Gisele, 23/10/2026)
 *
 * "Quando a última mensagem enviada é uma imagem, na prévia da conversa está
 * sendo apresentado um código. Alterar para apresentar apenas 'Imagem'."
 *
 * O "código" é o nome do arquivo: a api grava o anexo com nome gerado
 * (`MessageService::getFolderByFileType` o joga em `images/`), e a prévia caía
 * no último recurso do `rotuloDoAnexo` -- devolver o nome. Mesmo problema do
 * áudio no ponto 9, mesma solução.
 *
 * ## Por que esta lista é ESTREITA, ao contrário da do áudio
 *
 * Errar para mais aqui custa caro: um `contrato.pdf` rotulado "Imagem" esconde
 * o nome do arquivo, que nesse caso é informação de verdade -- documento tem
 * nome escolhido por alguém, imagem do celular não tem. Por isso só as
 * extensões que a api realmente aceita como imagem, mais `heic`/`heif`, que é
 * o que o iPhone manda pelo WhatsApp.
 */
const EXTENSOES_DE_IMAGEM = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif'];

export const ehImagem = (archive) => {
    const texto = String(archive || '').trim();

    if (texto === '') return false;

    // A imagem colada ou tirada na hora chega como data URL, sem extensão.
    if (texto.startsWith('data:image')) return true;

    const semQuery = texto.split('?')[0];
    const extensao = semQuery.split('.').pop().toLowerCase();

    return EXTENSOES_DE_IMAGEM.includes(extensao);
};

export const rotuloDoAnexo = (archive) => {
    const texto = String(archive || '').trim();

    if (texto === '') return null;

    if (ehAudio(texto)) return 'Áudio';

    if (ehImagem(texto)) return 'Imagem';

    // Sem extensão conhecida, o nome do arquivo ainda é a melhor informação
    // disponível: é o comportamento de antes, e não se perde nada.
    const semQuery = texto.split('?')[0];
    const partes = semQuery.split('/');

    return partes[partes.length - 1] || texto;
};

/**
 * A prévia da conversa para a fila do painel (ponto 12 da Gisele, 20/10/2026).
 *
 * Quem olha a fila está decidindo o que atender primeiro, e "Cliente — há 12
 * min" não diz se a pessoa mandou um "bom dia" ou um erro de emissão de nota.
 *
 * Anexo vira rótulo (`Áudio`, `contrato.pdf`) em vez do texto vazio que
 * acompanha a maioria deles. O resto é o texto sem marcação, numa linha só: a
 * mensagem pode vir com html do editor, e `<p>` colados virariam palavras
 * grudadas se as tags fossem removidas sem deixar espaço.
 */
export const previaDaConversa = (ultimaMensagem, limite = 90) => {
    if (!ultimaMensagem) return '';

    const anexo = String(ultimaMensagem.archive || '').trim();

    if (anexo !== '') return rotuloDoAnexo(anexo) || '';

    const texto = String(ultimaMensagem.message || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    if (texto === '') return '';

    return texto.length > limite ? `${texto.slice(0, limite - 1)}…` : texto;
};
